import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { ParseKeys } from "i18next"
import { z } from "zod"

import { CountryIso3166Schema } from "@tfg/types/schemas/enum"

import { i18n } from "~/i18n"
import {
  hasAtMostSixDecimalPlaces,
  validateLatitudeLongitudeTogether,
} from "~/pages/Admin/components/Fields/coordinatesSchema"
import { registrationIdItemFormSchema } from "~/utils/schemas/registration-id.schema"
import { preprocessOptionalNumberInput } from "~/utils/zod/utils"

const tSupplier = (key: ParseKeys<"supplier">): string =>
  i18n.t(key, { ns: "supplier" })

/** Validates a string is a valid country code (e.g. FR, EN) with a custom error key. */
export const countryIso3166FormSchema = z
  .string({
    error: tSupplier("productionSiteModal.errors.countryMustBeValid"),
  })
  .min(1, {
    message: tSupplier("productionSiteModal.errors.countryMustBeValid"),
  })
  .refine((val) => CountryIso3166Schema.safeParse(val).success, {
    message: tSupplier("productionSiteModal.errors.countryMustBeValid"),
  })
  .transform((val) => val as z.infer<typeof CountryIso3166Schema>)
/**
 * Form schema for create/edit production site (supplier portal).
 */

export const productionSiteGeneralInfoSchema = z.object({
  repositoryRecordId: z.uuid().optional().meta({
    fieldType: "hidden",
  }),
  name: z
    .string()
    .trim()
    .min(1, { message: tSupplier("productionSiteModal.errors.nameRequired") })
    .meta({
      label: "productionSiteModal.labels.name",
      span: 12,
      isRequired: true,
      tooltip: "productionSiteModal.tooltips.name",
      fieldType: "productionSiteRepositoryAutocomplete",
    }),
  parentCompany: z
    .string()
    .trim()
    .min(1, {
      message: tSupplier("productionSiteModal.errors.parentCompanyRequired"),
    })
    .meta({
      label: "productionSiteModal.labels.parentCompany",
      span: 12,
      tooltip: "productionSiteModal.tooltips.parentCompany",
      fieldType: "select",
      isRequired: true,
    }),
  registrationIds: z
    .array(registrationIdItemFormSchema)
    .optional()
    .meta({ span: 12, fieldType: "selectListRegistrationId" }),
  internalCode: z
    .string()
    .max(32, {
      message: tSupplier("productionSiteModal.errors.internalCodeTooLong"),
    })
    .meta({ label: "productionSiteModal.labels.internalCode", span: 12 })
    .optional(),
  performedActivities: z.array(z.string()).meta({
    span: 12,
    fieldType: "multiSelect",
  }),
})

const productionSiteLocationBaseFormSchema = z.object({
  street: z
    .string({
      error: tSupplier("productionSiteModal.errors.streetRequired"),
    })
    .trim()
    .min(1, { message: tSupplier("productionSiteModal.errors.streetRequired") })
    .min(2, { message: tSupplier("productionSiteModal.errors.streetTooShort") })
    .meta({ span: 6, isRequired: true }),
  city: z
    .string({
      error: tSupplier("productionSiteModal.errors.cityRequired"),
    })
    .trim()
    .min(1, { message: tSupplier("productionSiteModal.errors.cityRequired") })
    .meta({ span: 6, isRequired: true }),
  postalCode: z
    .string()
    .trim()
    .refine((value) => value.length === 0 || value.length >= 3, {
      message: tSupplier("productionSiteModal.errors.postalCodeTooShort"),
    })
    .optional()
    .meta({ span: 6 }),
  longitude: z
    .preprocess(
      preprocessOptionalNumberInput,
      z
        .number()
        .min(-180)
        .max(180)
        .refine(hasAtMostSixDecimalPlaces, {
          message: tSupplier("productionSiteModal.errors.coordinatesMaxDecimals"),
        })
        .optional()
    )
    .meta({ span: 6 }),
  latitude: z
    .preprocess(
      preprocessOptionalNumberInput,
      z
        .number()
        .min(-90)
        .max(90)
        .refine(hasAtMostSixDecimalPlaces, {
          message: tSupplier("productionSiteModal.errors.coordinatesMaxDecimals"),
        })
        .optional()
    )
    .meta({ span: 6 }),
  countryIso3166: countryIso3166FormSchema.meta({
    span: 6,
    isRequired: true,
    fieldType: "countrySelect",
  }),
})

export const productionSiteLocationFormSchema =
  productionSiteLocationBaseFormSchema.superRefine(
    validateLatitudeLongitudeTogether
  )

/** Merged schema for the full production site form (all tabs). */
export const productionSiteFormSchema = productionSiteGeneralInfoSchema.extend(
  productionSiteLocationBaseFormSchema.shape
)

export type ProductionSiteGeneralInfoFormValues = z.infer<
  typeof productionSiteGeneralInfoSchema
>
export type ProductionSiteLocationFormValues = z.infer<
  typeof productionSiteLocationFormSchema
>
export type ProductionSiteFormValues = z.infer<typeof productionSiteFormSchema>

export const useGetProductionSiteFormTabs = () => {
  const { t } = useTranslation("supplier")
  return useMemo(
    () =>
      [
        {
          key: "generalInfo",
          label: t("productionSiteModal.tabs.generalInfo"),
          fields: [
            "name",
            "parentCompany",
            "internalCode",
            "registrationIds",
            "performedActivities",
          ] as const,
        },
        {
          key: "location",
          label: t("productionSiteModal.tabs.location"),
          fields: [
            "street",
            "city",
            "postalCode",
            "countryIso3166",
            "longitude",
            "latitude",
          ] as const,
        },
      ] as const,
    [t]
  )
}
